import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const emptyForm = () => {
  const now = new Date();
  return { customer_id: '', month: String(now.getMonth() + 1), year: String(now.getFullYear()), subscription_end_date: '', source_device_count: '', price1: '', price2: '', price3: '', devices: '' };
};

export function CreateCaseDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm());
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setSearch('');
    if (!customers.length) {
      axios.get(`${API_BASE}/api/customers?token=${token}`).then(r => setCustomers(r.data)).catch(() => toast.error('Müşteriler yüklenemedi'));
    }
  }, [open]);

  const deviceList = form.devices.split('\n').map(d => d.trim()).filter(Boolean);
  const matches = search.length >= 2 ? customers.filter(c => c.name?.toLowerCase().includes(search.toLowerCase())).slice(0, 8) : [];
  const selected = customers.find(c => c.id === form.customer_id);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (submitting) return;
    const count = deviceList.length || parseInt(form.source_device_count || '0', 10);
    if (!form.customer_id) return toast.error('Müşteri seçiniz');
    if (!form.subscription_end_date) return toast.error('Abonelik bitiş tarihi giriniz');
    if (count <= 0) return toast.error('Cihaz adedi veya cihaz listesi giriniz');
    if (new Set(deviceList).size !== deviceList.length) return toast.error('Cihaz listesinde tekrar eden kayıt var');
    const toK = (v) => Math.round(parseFloat(String(v || '0').replace(',', '.')) * 100) || 0;
    const unit_prices = { '1': toK(form.price1), '2': toK(form.price2), '3': toK(form.price3) };
    const expected = unit_prices['1'] * count;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/subscription-management/cases?token=${token}`, {
        customer_id: form.customer_id,
        period: `${MONTHS[parseInt(form.month, 10) - 1]} ${form.year}`,
        period_year: parseInt(form.year, 10),
        period_month: parseInt(form.month, 10),
        subscription_end_date: form.subscription_end_date,
        source_device_count: count,
        expected_revenue: expected,
        unit_prices,
        devices: deviceList
      });
      toast.success('Dosya oluşturuldu');
      onCreated(res.data.id);
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  const years = [0, 1, 2].map(i => String(new Date().getFullYear() - 1 + i));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="create-case-dialog">
        <DialogHeader><DialogTitle>Yeni Abonelik Dosyası</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Müşteri *</Label>
            {selected ? (
              <div className="flex items-center justify-between border rounded-md px-3 py-2 text-sm" data-testid="create-case-selected-customer">
                {selected.name}
                <button type="button" className="text-xs text-blue-600" onClick={() => setForm({ ...form, customer_id: '' })}>Değiştir</button>
              </div>
            ) : (
              <>
                <Input data-testid="create-case-customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Müşteri adı yazın (en az 2 harf)" />
                {matches.length > 0 && (
                  <div className="border rounded-md mt-1 divide-y max-h-48 overflow-y-auto">
                    {matches.map(c => (
                      <button key={c.id} type="button" data-testid={`create-case-customer-${c.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50" onClick={() => setForm({ ...form, customer_id: c.id })}>{c.name}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Dönem Ayı</Label>
              <Select value={form.month} onValueChange={(v) => setForm({ ...form, month: v })}>
                <SelectTrigger data-testid="create-case-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Yıl</Label>
              <Select value={form.year} onValueChange={(v) => setForm({ ...form, year: v })}>
                <SelectTrigger data-testid="create-case-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Abonelik Bitiş Tarihi *</Label>
            <Input data-testid="create-case-end-date-input" type="date" value={form.subscription_end_date} onChange={set('subscription_end_date')} />
          </div>
          <div>
            <Label>Cihaz Listesi (her satıra bir plaka / seri no)</Label>
            <Textarea data-testid="create-case-devices-input" value={form.devices} onChange={set('devices')} rows={4} placeholder={'34ABC01\n34ABC02'} />
            <p className="text-xs text-gray-500 mt-1">Liste girilirse cihaz adedi otomatik {deviceList.length} olur; boş bırakılırsa adet girin.</p>
          </div>
          {deviceList.length === 0 && (
            <div>
              <Label>Aboneliği Dolan Cihaz Adedi *</Label>
              <Input data-testid="create-case-device-count-input" type="number" min="1" value={form.source_device_count} onChange={set('source_device_count')} />
            </div>
          )}
          <div>
            <Label>Cihaz Başı Yenileme Fiyatları (TL, KDV dahil)</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {['1', '2', '3'].map(t => (
                <div key={t}>
                  <span className="text-xs text-gray-500">{t} Yıl</span>
                  <Input data-testid={`create-case-price-${t}-input`} type="number" min="0" step="0.01" value={form[`price${t}`]} onChange={set(`price${t}`)} />
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">Beklenen gelir 1 yıllık fiyat × cihaz adedi olarak hesaplanır.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button data-testid="create-case-submit-btn" onClick={submit} disabled={submitting}>{submitting ? 'Kaydediliyor...' : 'Oluştur'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
